import { observer } from 'mobx-react';
import { useState } from 'react';

import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import { makeStyles } from '@bequestinc/wui';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import {
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { useToggledState } from 'hooks';

import parseErrors from 'utils/errors';

const useStyles = makeStyles()(theme => ({
  text: {
    color: theme.palette.primary.main,
    '&:hover, &:focus': {
      textDecoration: 'underline',
    },
  },
  listForTable: {
    width: '100px',
    spacing: '0.5em',
    marginLeft: 0,
  },
  textForTable: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    color: theme.palette.primary.main,
    '&:hover, &:focus': {
      textDecoration: 'underline',
    },
  },
  tableButton: {
    fontSize: '11px',
    height: '25px',
    justifyContent: 'flex-start',
    '& .MuiButton-label': { textAlign: 'left', width: '100%' },
  },
}));

const UploadedDocuments = ({
  uploadedDocuments,
  setUploadedDocuments,
  allowDownload,
  includeLabel,
  getDocument,
  deleteDocument,
  removeFromPayload,
  forTable,
}) => {
  const { classes } = useStyles();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [showAllDocuments, toggleShowAllDocuments] = useToggledState(false);
  const [documentToView, setDocumentToView] = useState({ filename: '', url: '', isBlob: false });
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const openDocumentViewer = async (doc, index) => {
    if (doc.notUploaded) {
      setDocumentToView({
        url: URL.createObjectURL(doc.file),
        filename: doc.filename,
        isBlob: true,
      });
      setDocumentViewerOpen(true);
    } else {
      setLoading(index);
      getDocument(doc.id)
        .then(async response => {
          setDocumentToView({ data: await response.data.arrayBuffer(), filename: doc.filename });
          setDocumentViewerOpen(true);
          setError('');
        })
        .catch(e => {
          if (e.response.status === 400) {
            setError(parseErrors(e.response));
          } else {
            setError('Error fetching document! Please try again');
          }
        })
        .finally(() => setLoading(null));
    }
  };

  const DocumentViewer = dynamic(() => import('./DocumentViewer'), { ssr: false });

  const onRemove = (doc, idx) => {
    const removeFromList = () => {
      setUploadedDocuments([
        ...uploadedDocuments.slice(0, idx),
        ...uploadedDocuments.slice(idx + 1),
      ]);
    };
    if (doc.notUploaded || removeFromPayload) {
      removeFromList();
    } else {
      deleteDocument(doc.id).then(() => {
        removeFromList();
      });
    }
  };

  const downloadDoc = doc => {
    const link = document.createElement('a');
    getDocument(doc.id)
      .then(response => {
        link.href = URL.createObjectURL(response.data);
      })
      .then(() => {
        link.download = doc.filename;
        link.click();
      })
      .catch(() => setError('Error downloading document! Please try again'));
  };

  const documentItemDisplay = (doc, index) => {
    return (
      <Grid
        item
        container
        direction="row"
        justifyContent="flex-start"
        key={index}
        alignItems="center"
      >
        {loading === index && <CircularProgress size="1rem" />}
        {deleteDocument ||
          (removeFromPayload && (
            <Grid item>
              <IconButton
                onClick={() => onRemove(doc, index)}
                size="large"
                disabled={loading != null}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          ))}
        <Grid container item spacing={1} direction="row">
          {allowDownload && (
            <Grid item xs={forTable ? 3 : 1.5}>
              <Button
                className={forTable ? classes.tableButton : ''}
                onClick={() => downloadDoc(doc)}
                disabled={loading != null}
              >
                <FileDownloadOutlinedIcon />
              </Button>
            </Grid>
          )}
          <Grid item xs={6}>
            <Tooltip
              title={forTable ? doc.filename : ''}
              placement="left-start"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 20],
                      },
                    },
                  ],
                },
              }}
            >
              <Button
                className={forTable ? classes.tableButton : ''}
                onClick={() => openDocumentViewer(doc, index)}
                disabled={loading != null}
              >
                <Typography className={forTable ? classes.textForTable : classes.text}>
                  {forTable && doc.filename?.length > 25
                    ? `${doc.filename.substring(0, 25)}...`
                    : doc.filename}
                </Typography>
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid
        container
        direction="column"
        alignItems="flex-start"
        className={forTable ? classes.listForTable : ''}
      >
        {includeLabel && (
          <Grid item>
            <Typography>
              <b>Documents:</b>
            </Typography>
          </Grid>
        )}
        {forTable && uploadedDocuments.length > 2 ? (
          <Stack direction="column">
            {uploadedDocuments.slice(0, 2).map((doc, index) => (
              <>{documentItemDisplay(doc, index)}</>
            ))}
            <Collapse in={showAllDocuments}>
              {uploadedDocuments.slice(2).map((doc, index) => (
                <>{documentItemDisplay(doc, index)}</>
              ))}
            </Collapse>
            <Button
              onClick={toggleShowAllDocuments}
              sx={{
                fontSize: '10pt',
                color: 'primary',
                justifyContent: 'flex-start',
                width: '75%',
              }}
            >
              {!showAllDocuments ? (
                <KeyboardArrowDownRoundedIcon color="primary" />
              ) : (
                <KeyboardArrowUpRoundedIcon color="primary" />
              )}
              <Typography color="primary" fontSize="10pt" fontWeight="bold" sx={{ paddingLeft: 3 }}>
                {showAllDocuments ? 'hide' : 'show all'}
              </Typography>
            </Button>
          </Stack>
        ) : (
          <>
            {uploadedDocuments.map((doc, index) => (
              <>{documentItemDisplay(doc, index)}</>
            ))}
          </>
        )}
        {error && (
          <Grid item>
            <Typography color="error" fontWeight="bold">
              {error}
            </Typography>
          </Grid>
        )}
      </Grid>

      <DocumentViewer
        document={documentToView}
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
      />
    </>
  );
};

UploadedDocuments.propTypes = {
  uploadedDocuments: PropTypes.arrayOf(PropTypes.object).isRequired,
  setUploadedDocuments: PropTypes.func,
  allowDownload: PropTypes.bool,
  includeLabel: PropTypes.bool,
  getDocument: PropTypes.func,
  removeFromPayload: PropTypes.bool,
  deleteDocument: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  forTable: PropTypes.bool,
};

UploadedDocuments.defaultProps = {
  includeLabel: true,
  setUploadedDocuments: () => {},
  allowDownload: true,
  getDocument: () => {},
  deleteDocument: null,
  removeFromPayload: false,
  forTable: false,
};

export default observer(UploadedDocuments);
